"use client"

import * as React from "react"
import { useLocation, useNavigate } from "react-router"

import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu"
import { useExamRole, type UserRole } from "@/contexts/exam-role-context"
import { useProduct } from "@/contexts/product-context"
import {
  EXAM_MANAGEMENT_ADMIN_HOME_PATH,
  EXAM_MANAGEMENT_MY_EXAMS_PATH,
} from "@/lib/exam-management-paths"
import {
  isExamManagementStudentAllowedPath,
} from "@/lib/exam-management-student-routes"

const EXAM_PERSONAS: readonly {
  value: UserRole
  label: string
  iconClass: string
}[] = [
  { value: "Student", label: "Student", iconClass: "fa-light fa-graduation-cap" },
  { value: "Faculty", label: "Faculty", iconClass: "fa-light fa-chalkboard-user" },
  { value: "Admin", label: "Admin", iconClass: "fa-light fa-shield-halved" },
]

function personaLabel(role: UserRole): string {
  return EXAM_PERSONAS.find(entry => entry.value === role)?.label ?? role
}

/** Switch Exam Management sidebar between student and admin/faculty layouts. */
export function ExamManagementPersonaMenuItem() {
  const { product } = useProduct()
  const { role, setRole } = useExamRole()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  if (product !== "exxat-exam-management") return null

  function switchPersona(nextRole: UserRole) {
    if (nextRole === role) return
    setRole(nextRole)

    if (nextRole === "Student") {
      if (!isExamManagementStudentAllowedPath(pathname)) {
        navigate(EXAM_MANAGEMENT_MY_EXAMS_PATH, { replace: true })
      }
      return
    }

    if (nextRole !== "Student" && isExamManagementStudentAllowedPath(pathname)) {
      navigate(EXAM_MANAGEMENT_ADMIN_HOME_PATH, { replace: true })
    }
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <i className="fa-light fa-id-badge" aria-hidden="true" />
        Exam persona
        <span className="ms-auto pe-1 text-xs text-muted-foreground">
          {personaLabel(role)}
        </span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-56">
        <DropdownMenuLabel>Persona</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={role}
          onValueChange={value => switchPersona(value as UserRole)}
        >
          {EXAM_PERSONAS.map(entry => (
            <DropdownMenuRadioItem key={entry.value} value={entry.value}>
              <i className={entry.iconClass} aria-hidden="true" />
              {entry.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}
